import { api } from './client';
import type { Page } from './catalogs';

// ==================== Inventory ====================

export interface TPInventoryItem {
  id?: string;
  line_number: number;
  nomenclature_id: string;
  characteristic_id?: string | null;
  series_id?: string | null;
  quantity: string;
  original_unit_price: string;
  old_tax_purpose_id: string;
  new_tax_purpose_id: string;
  vat_rate?: string;
  vat_amount_to_compensate?: string;
  line_comment?: string | null;
}

export interface TPInventory {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  posted: boolean;
  posted_at: string | null;
  deletion_mark: boolean;
  comment: string | null;
  adjustment_reason: string;
  total_vat_to_compensate: string;
  bulk_old_tax_purpose_id: string | null;
  bulk_new_tax_purpose_id: string | null;
  items: TPInventoryItem[];
  created_at: string;
  updated_at: string;
  external_source?: string | null;
}

export interface TPInventoryCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  comment?: string | null;
  adjustment_reason: string;
  bulk_old_tax_purpose_id?: string | null;
  bulk_new_tax_purpose_id?: string | null;
  items: TPInventoryItem[];
}

export const tpInventory = {
  list: (params?: { organization_id?: string; posted?: boolean;
    sort_by?: string; sort_dir?: 'asc' | 'desc'; limit?: number; offset?: number }) =>
    api.get<Page<TPInventory>>('/documents/tax-purpose-adj-inventory', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<TPInventory>(`/documents/tax-purpose-adj-inventory/${id}`).then((r) => r.data),
  create: (payload: TPInventoryCreate) =>
    api.post<TPInventory>('/documents/tax-purpose-adj-inventory', payload).then((r) => r.data),
  update: (id: string, payload: Partial<TPInventoryCreate>) =>
    api.patch<TPInventory>(`/documents/tax-purpose-adj-inventory/${id}`, payload).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/documents/tax-purpose-adj-inventory/${id}`).then(() => undefined),
  addItem: (id: string, payload: TPInventoryItem) =>
    api.post<TPInventoryItem>(`/documents/tax-purpose-adj-inventory/${id}/items`, payload).then((r) => r.data),
  removeItem: (id: string, itemId: string) =>
    api.delete(`/documents/tax-purpose-adj-inventory/${id}/items/${itemId}`).then(() => undefined),
  post: (id: string) =>
    api.post<TPInventory>(`/documents/tax-purpose-adj-inventory/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<TPInventory>(`/documents/tax-purpose-adj-inventory/${id}/unpost`).then((r) => r.data),
};

// ==================== TZR ====================

export interface TPTzr {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  posted: boolean;
  posted_at: string | null;
  deletion_mark: boolean;
  comment: string | null;
  adjustment_reason: string;
  related_goods_receipt_id: string;
  tzr_amount: string;
  old_tax_purpose_id: string;
  new_tax_purpose_id: string;
  vat_rate: string;
  vat_amount_to_compensate: string;
  created_at: string;
  updated_at: string;
  external_source?: string | null;
}

export interface TPTzrCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  comment?: string | null;
  adjustment_reason: string;
  related_goods_receipt_id: string;
  tzr_amount: string;
  old_tax_purpose_id: string;
  new_tax_purpose_id: string;
  vat_rate?: string;
}

export const tpTzr = {
  list: (params?: { organization_id?: string; posted?: boolean;
    sort_by?: string; sort_dir?: 'asc' | 'desc'; limit?: number; offset?: number }) =>
    api.get<Page<TPTzr>>('/documents/tax-purpose-adj-tzr', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<TPTzr>(`/documents/tax-purpose-adj-tzr/${id}`).then((r) => r.data),
  create: (payload: TPTzrCreate) =>
    api.post<TPTzr>('/documents/tax-purpose-adj-tzr', payload).then((r) => r.data),
  update: (id: string, payload: Partial<TPTzrCreate>) =>
    api.patch<TPTzr>(`/documents/tax-purpose-adj-tzr/${id}`, payload).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/documents/tax-purpose-adj-tzr/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<TPTzr>(`/documents/tax-purpose-adj-tzr/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<TPTzr>(`/documents/tax-purpose-adj-tzr/${id}/unpost`).then((r) => r.data),
};

// ==================== Expected vs Confirmed ====================

export type AdjustmentType =
  | 'REGISTERED_WITH_DIFFERENCE'
  | 'NOT_REGISTERED'
  | 'CUSTOMS_RECALC'
  | 'OTHER';

export interface EVCVA {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  posted: boolean;
  posted_at: string | null;
  deletion_mark: boolean;
  comment: string | null;
  adjustment_type: AdjustmentType;
  original_tax_invoice_outgoing_id: string | null;
  original_tax_invoice_incoming_id: string | null;
  expected_amount: string;
  confirmed_amount: string;
  difference: string;
  adjustment_reason: string;
  created_at: string;
  updated_at: string;
  external_source?: string | null;
}

export interface EVCVACreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  comment?: string | null;
  adjustment_type: AdjustmentType;
  original_tax_invoice_outgoing_id?: string | null;
  original_tax_invoice_incoming_id?: string | null;
  expected_amount: string;
  confirmed_amount?: string;
  adjustment_reason: string;
}

export const evcva = {
  list: (params?: {
    organization_id?: string;
    adjustment_type?: AdjustmentType;
    posted?: boolean;
    sort_by?: string;
    sort_dir?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
  }) =>
    api.get<Page<EVCVA>>('/documents/expected-confirmed-vat-adj', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<EVCVA>(`/documents/expected-confirmed-vat-adj/${id}`).then((r) => r.data),
  create: (payload: EVCVACreate) =>
    api.post<EVCVA>('/documents/expected-confirmed-vat-adj', payload).then((r) => r.data),
  update: (id: string, payload: Partial<EVCVACreate>) =>
    api.patch<EVCVA>(`/documents/expected-confirmed-vat-adj/${id}`, payload).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/documents/expected-confirmed-vat-adj/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<EVCVA>(`/documents/expected-confirmed-vat-adj/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<EVCVA>(`/documents/expected-confirmed-vat-adj/${id}/unpost`).then((r) => r.data),
};
